import { useCallback, useEffect, useRef, useState } from 'react';
import { getOtoSessions, getOtoSession, createOtoSession, archiveOtoSession, streamOtoMessage, confirmOtoProposal, cancelOtoProposal } from '../services/api/oto';
import { formatApiError } from '../utils/apiError';

export const useOtoConversation = () => {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [acting, setActing] = useState(null);
  const [memorySignal, setMemorySignal] = useState(0);
  const streamRef = useRef(null);
  const currentRef = useRef(null);
  const loadVersion = useRef(0);
  const pending = useRef(false);
  const actionPending = useRef(false);
  // A confirmed HTTP result must not regress because of an older session snapshot.
  const resolvedProposals = useRef(new Map());
  const reconcileProposals = useCallback((items, sessionId) => {
    const merged = new Map(items.map(item => {
      const resolved = resolvedProposals.current.get(item.id);
      return [item.id, resolved && ['pending', 'executing'].includes(item.state) ? resolved : item];
    }));
    resolvedProposals.current.forEach(item => {
      if (item.session_id === sessionId && !merged.has(item.id)) merged.set(item.id, item);
    });
    return [...merged.values()];
  }, []);
  const updateProposal = proposal => {
    if (!['pending', 'executing'].includes(proposal.state)) resolvedProposals.current.set(proposal.id, proposal);
    setProposals(items => reconcileProposals([...items.filter(item => item.id !== proposal.id), proposal], proposal.session_id));
  };
  const list = useCallback(async () => { const response = await getOtoSessions(); setSessions(response.data.items); return response.data.items; }, []);
  const open = useCallback(async session => {
    if (pending.current) return;
    const version = ++loadVersion.current;
    setLoading(true); setError('');
    try {
      const response = await getOtoSession(session.id);
      if (version !== loadVersion.current) return;
      currentRef.current = response.data.session; setCurrent(response.data.session);
      const proposals = reconcileProposals(response.data.proposals, session.id);
      setMessages(response.data.messages); setProposals(proposals);
      return { ...response.data, proposals };
    } catch (err) { if (version === loadVersion.current) setError(formatApiError(err, 'Discussion indisponible.')); }
    finally { if (version === loadVersion.current) setLoading(false); }
  }, [reconcileProposals]);
  useEffect(() => {
    let alive = true;
    list().then(items => { if (alive && items.length) open(items[0]); else if (alive) setLoading(false); })
      .catch(err => { if (alive) { setError(formatApiError(err, 'OTO est indisponible.')); setLoading(false); } });
    return () => { alive = false; streamRef.current?.abort(); loadVersion.current += 1; };
  }, [list, open]);
  const newSession = async () => {
    if (pending.current || actionPending.current) return;
    setLoading(true); setError('');
    try { const response = await createOtoSession(); await list(); await open(response.data); return response.data; }
    catch (err) { setError(formatApiError(err)); setLoading(false); }
  };
  const send = async (text, editedFrom = null, attachments = null) => {
    if (pending.current || actionPending.current || (!text.trim() && !attachments?.length)) return false;
    text = text.trim() || 'Analyse les fichiers joints.';
    pending.current = true; setBusy(true); setError(''); setStatus('OTO réfléchit…');
    const controller = new AbortController(); streamRef.current = controller;
    let session = currentRef.current;
    const userId = `local-user-${Date.now()}`;
    let assistantId = `local-assistant-${Date.now()}`;
    let finalError = '';
    try {
      if (!session) {
        session = (await createOtoSession()).data;
        currentRef.current = session; setCurrent(session); setMessages([]); setProposals([]);
      }
      setMessages(items => [...items, { id: userId, role: 'user', text, created_at: new Date().toISOString(), records: [], proposal_ids: [], edited_from: editedFrom, response_only: Boolean(editedFrom), attachments: attachments || [] },
        { id: assistantId, role: 'assistant', text: '', created_at: new Date().toISOString(), records: [], proposal_ids: [] }]);
      await streamOtoMessage(session.id, text, event => {
        if (event.type === 'meta') {
          const previous = assistantId; assistantId = event.message_id;
          setMessages(items => items.map(item => item.id === previous ? { ...item, id: assistantId } : item));
        }
        if (event.type === 'text') { setStatus('Réponse en cours…'); setMessages(items => items.map(item => item.id === assistantId ? { ...item, text: item.text + event.content } : item)); }
        if (event.type === 'status') setStatus(event.text);
        if (event.type === 'proposal') {
          updateProposal(event.proposal);
          setMessages(items => items.map(item => item.id === assistantId ? { ...item, proposal_ids: [...item.proposal_ids, event.proposal.id] } : item));
        }
        if (event.type === 'memory') {
          setMemorySignal(value => value + 1);
          setMessages(items => items.map(item => item.id === assistantId ? { ...item, memory_ids: [...new Set([...(item.memory_ids || []), event.memory.id])] } : item));
        }
        if (event.type === 'records') setMessages(items => items.map(item => item.id === assistantId ? { ...item, records: [...item.records, event.records] } : item));
        if (event.type === 'error') { finalError = event.message; setError(event.message); }
        if (event.type === 'done') setMessages(items => items.map(item => item.id === assistantId ? event.message : item));
      }, controller.signal, editedFrom, attachments?.map(file => file.id) ?? null);
      return true;
    } catch (err) {
      finalError = err.name !== 'AbortError' ? err.message || 'La réponse a été interrompue.' : 'Réponse interrompue. Les propositions éventuelles restent non exécutées.';
      setError(finalError);
      return false;
    } finally {
      pending.current = false; setBusy(false); setStatus(''); streamRef.current = null;
      await list().catch(() => {});
      if (session) await open(session);
      if (finalError) setError(finalError);
    }
  };
  const act = async (proposal, type) => {
    if (pending.current || actionPending.current) return;
    actionPending.current = true;
    loadVersion.current += 1;
    setActing(proposal.id); setError('');
    try {
      const response = type === 'confirm' ? await confirmOtoProposal(proposal) : await cancelOtoProposal(proposal.id);
      updateProposal(response.data);
      if (currentRef.current) await open(currentRef.current);
      return response.data;
    } catch (err) {
      const refreshed = currentRef.current && await open(currentRef.current);
      const resolved = refreshed?.proposals.find(item => item.id === proposal.id);
      if (resolved && ['completed', 'cancelled'].includes(resolved.state)) return resolved;
      setError(formatApiError(err, 'Action non confirmée. Vérifiez le CRM avant de réessayer.')); return null;
    }
    finally { actionPending.current = false; setActing(null); }
  };
  const archive = async () => {
    if (!current || busy || acting) return;
    try {
      await archiveOtoSession(current.id); const items = await list();
      currentRef.current = null; setCurrent(null); setMessages([]); setProposals([]);
      if (items.length) await open(items[0]);
    } catch (err) { setError(formatApiError(err)); }
  };
  return { sessions, current, messages, proposals, busy, loading, status, error, acting, memorySignal, open, newSession, send, act, archive,
    interrupt: () => streamRef.current?.abort(), refresh: () => currentRef.current && open(currentRef.current) };
};